import { useEffect, useRef, useState, useCallback } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import TimeAgo from "react-timeago";
import { useTheme } from "@mui/material";
import {
  updateQuery,
  updateQueryEndDate,
  updateQueryStartDate,
  updateQueryType,
  resetQuery,
} from "../redux/querySlice";
import gsap from "gsap";
import useApiRequest from "../common/APIHandler/makeApiRequest";
import { useNavigate } from "react-router-dom";
import { updateChatID } from "../redux/threadSlice";

const AggregatedFeed = () => {
  const theme = useTheme();
  const endpoint = "get-feed";
  const payload = {
    max_items: 100,
  };

  const headers = {
    accept: "application/json",
    // "accept-encoding": "gzip, deflate, br, zstd",
    "accept-language": "en-US,en;q=0.9",
    // origin: "http://localhost:2000",
    // referer: "http://localhost:2000/",
    priority: "u=1, i",
    Authorization: sessionStorage.getItem("token"),
  };

  const {
    response: feedData,
    loading: feedLoading,
    error: feedError,
    makeApiRequest,
  } = useApiRequest(endpoint, "POST", {}, payload, headers);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryTextRedux = useSelector((state) => state.queryText.value);

  const [feedTopics, setFeedTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Track the current scroll phase: "scrolling-down", "paused-bottom", "scrolling-up", "paused-top"
  const [isMouseOverFeed, setIsMouseOverFeed] = useState(false);
  const [scrollPhase, setScrollPhase] = useState("scrolling-down");
  const [hasManuallyScrolled, setHasManuallyScrolled] = useState(false);

  const scrollerRef = useRef(null);
  const scrollAnimationRef = useRef(null);
  const timeoutRef = useRef(null);
  const prevFeedTopicsRef = useRef(feedTopics);

  const handleFeedClick = (text, queryDateRange) => {
    dispatch(updateQueryStartDate(queryDateRange["startTimeEpochMillis"]));
    dispatch(updateQueryEndDate(queryDateRange["endTimeEpochMillis"]));
    dispatch(updateQuery(text));
    dispatch(updateQueryType("feedQuery"));
    dispatch(updateChatID(""));
  };

  // Clean up any existing animations and timeouts
  const cleanupAnimations = useCallback(() => {
    if (scrollAnimationRef.current) {
      scrollAnimationRef.current.kill();
      scrollAnimationRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Track manual scrolling
  const startScrollAnimation = useCallback(() => {
    if (!scrollerRef.current || feedTopics.length === 0 || isMouseOverFeed) {
      return;
    }

    // Clean up any existing animations and timeouts
    cleanupAnimations();

    const scroller = scrollerRef.current;
    const currentScrollTop = scroller.scrollTop;
    const totalScrollHeight = scroller.scrollHeight - scroller.clientHeight;

    // Handle different scroll phases
    switch (scrollPhase) {
      case "scrolling-down":
        // Calculate scroll duration based on content amount
        const remainingDistance = totalScrollHeight - currentScrollTop;
        const fullDuration = feedTopics.length * 1.5;
        const adjustedDuration = Math.max(
          1,
          (remainingDistance / totalScrollHeight) * fullDuration
        );

        // Create animation to scroll down
        scrollAnimationRef.current = gsap.to(scroller, {
          scrollTop: totalScrollHeight,
          duration: adjustedDuration,
          ease: "none",
          onComplete: () => {
            setScrollPhase("paused-bottom");
            startScrollAnimation();
          },
        });
        break;

      case "paused-bottom":
        // Pause at the bottom for 3 seconds
        timeoutRef.current = setTimeout(() => {
          setScrollPhase("scrolling-up");
          startScrollAnimation();
        }, 1000);
        break;

      case "scrolling-up":
        // Smoothly scroll back to the top
        scrollAnimationRef.current = gsap.to(scroller, {
          scrollTop: 0,
          duration: 1.5,
          ease: "power2.inOut",
          onComplete: () => {
            setScrollPhase("paused-top");
            startScrollAnimation();
          },
        });
        break;

      case "paused-top":
        // Pause at the top for 2 seconds
        timeoutRef.current = setTimeout(() => {
          setScrollPhase("scrolling-down");
          startScrollAnimation();
        }, 1000);
        break;

      default:
        // Reset to scrolling down if in an unknown state
        setScrollPhase("scrolling-down");
        startScrollAnimation();
    }
  }, [cleanupAnimations, feedTopics?.length, isMouseOverFeed, scrollPhase]);

  useEffect(() => {
    setFeedTopics(feedData);
    if (feedData?.length > 0) setLoading(false);
  }, [feedData]);

  useEffect(() => {
    makeApiRequest();
    const intervalId = setInterval(() => {
      makeApiRequest();
    }, 15000);

    return () => clearInterval(intervalId);
  }, []);

  // Monitor for manual scrolling
  // useEffect(() => {
  //   if (scrollerRef.current) {
  //     const scroller = scrollerRef.current;
  //     let scrollTimeout;

  //     const handleScroll = () => {
  //       if (isMouseOverFeed) {
  //         // Clear existing timeout
  //         if (scrollTimeout) {
  //           clearTimeout(scrollTimeout);
  //         }

  //         // Set a new timeout to detect when scrolling stops
  //         scrollTimeout = setTimeout(() => {
  //           setHasManuallyScrolled(true);
  //         }, 150);
  //       }
  //     };

  //     const handleMouseLeave = () => {
  //       if (hasManuallyScrolled) {
  //         startScrollAnimation();
  //         setHasManuallyScrolled(false);
  //       }
  //     };

  //     scroller.addEventListener("scroll", handleScroll);
  //     scroller.addEventListener("mouseleave", handleMouseLeave);

  //     return () => {
  //       scroller.removeEventListener("scroll", handleScroll);
  //       scroller.removeEventListener("mouseleave", handleMouseLeave);
  //       if (scrollTimeout) {
  //         clearTimeout(scrollTimeout);
  //       }
  //     };
  //   }
  // }, [hasManuallyScrolled, isMouseOverFeed, startScrollAnimation]);

  // Set up autoscroll animation
  // useEffect(() => {
  //   // if (scrollerRef.current && feedTopics?.length > 0) {
  //   if (false) {
  //     // Start the autoscroll animation
  //     startScrollAnimation();

  //     // Add event listeners for hover
  //     const scroller = scrollerRef.current;

  //     const handleMouseEnter = () => {
  //       setIsMouseOverFeed(true);
  //       cleanupAnimations();
  //     };

  //     const handleMouseLeave = () => {
  //       setIsMouseOverFeed(false);
  //       if (!hasManuallyScrolled) {
  //         startScrollAnimation();
  //       }
  //     };

  //     scroller.addEventListener("mouseenter", handleMouseEnter);
  //     scroller.addEventListener("mouseleave", handleMouseLeave);

  //     // Cleanup function
  //     return () => {
  //       scroller.removeEventListener("mouseenter", handleMouseEnter);
  //       scroller.removeEventListener("mouseleave", handleMouseLeave);
  //       cleanupAnimations();
  //     };
  //   }
  // }, [feedTopics, startScrollAnimation, cleanupAnimations]);

  // Handle new content arrival
  // useEffect(() => {
  //   if (
  //     prevFeedTopicsRef.current?.length !== 0 &&
  //     feedTopics?.length > 0 &&
  //     prevFeedTopicsRef.current?.[0]?.id !== feedTopics?.[0]?.id
  //   ) {
  //     // Only scroll to top if mouse is not over feed
  //     if (!isMouseOverFeed) {
  //       // Temporarily pause autoscroll
  //       cleanupAnimations();

  //       // Scroll to top
  //       if (scrollerRef.current) {
  //         gsap.to(scrollerRef.current, {
  //           duration: 0.5,
  //           scrollTop: 0,
  //           onComplete: () => {
  //             // Reset to scrolling down phase and start from top after new content arrives
  //             setScrollPhase("paused-top");
  //             startScrollAnimation();
  //           },
  //         });
  //       }
  //     }
  //   }
  //   prevFeedTopicsRef.current = feedTopics;
  // }, [
  //   feedTopics,
  //   isMouseOverFeed,
  //   cleanupAnimations,
  //   startScrollAnimation,
  //   setScrollPhase,
  // ]);

  return (
    <Box
      id="aggregated-feed-container"
      sx={{ flex: 1, background: theme.palette.primary.dark }}
    >
      <Typography variant="h5" fontWeight={700} marginInline={"1.5rem"}>
        Feed
      </Typography>
      <Box id="aggregated-feed-scroller" ref={scrollerRef}>
        {loading && (
          <Box className="aggregated-feed-loader">
            <CircularProgress
              size={36}
              thickness={6}
              sx={{
                color: "#AC1FFF",
              }}
            />
          </Box>
        )}
        <ul className="aggregated-feed-list">
          {feedTopics?.length > 0 &&
            feedTopics.map((topic) => {
              return (
                <li
                  key={topic?.id}
                  className="aggregated-feed-item"
                  onClick={() =>
                    handleFeedClick(topic.display_text, topic.queryDateRange)
                  }
                >
                  {/* <img src={topic?.thumbnail_url} alt="feed_image" /> */}
                  <Box style={{ padding: "10px" }}>
                    <Typography variant="h7" fontWeight={700}>
                      {topic.display_text}
                    </Typography>
                    <br />
                    <Typography variant="caption" fontWeight={500}>
                      <TimeAgo
                        date={Number(
                          topic.queryDateRange["endTimeEpochMillis"]
                        )}
                      />
                    </Typography>
                  </Box>
                </li>
              );
            })}
        </ul>
      </Box>
    </Box>
  );
};

export default AggregatedFeed;
